import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { href: '#szolgaltatasok', label: 'Szolgáltatások' },
    { href: '#rolam', label: 'Rólam' },
    { href: '#folyamat', label: 'Folyamat' },
    { href: '#eredmenyek', label: 'Eredmények' },
    { href: '#kapcsolat', label: 'Kapcsolat' },
  ]

  const scrollToSection = (href) => {
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
    setIsMobileMenuOpen(false)
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-cream/90 glass-nav shadow-[0_2px_20px_rgba(43,27,20,0.08)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="logo-link"
          >
            <span className="font-heading text-2xl md:text-3xl font-bold text-coffee-900">
              KV
            </span>
            <span className="font-body text-lg text-coffee-600">Studio</span>
          </motion.a>

          {/* Desktop Navigation */}
          <motion.ul
            className="hidden md:flex items-center gap-8"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {navLinks.map((link) => (
              <li key={link.href}>
                <button
                  onClick={() => scrollToSection(link.href)}
                  className="text-coffee-700 hover:text-coffee-900 font-medium text-sm tracking-wide transition-colors duration-200 relative group"
                  data-testid={`nav-link-${link.href.slice(1)}`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-coffee-600 transition-all duration-300 group-hover:w-full" />
                </button>
              </li>
            ))}
          </motion.ul>

          {/* CTA Button */}
          <motion.button
            onClick={() => scrollToSection('#kapcsolat')}
            className="hidden md:flex items-center gap-2 bg-coffee-900 text-cream px-6 py-2.5 rounded-full font-medium text-sm hover:bg-coffee-800 transition-colors duration-200 btn-primary"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="nav-cta-button"
          >
            Kérj ajánlatot
          </motion.button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-coffee-900"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-cream/95 glass-nav rounded-2xl mt-2 overflow-hidden"
            >
              <ul className="py-4 px-4 space-y-2">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="block w-full text-left py-3 px-4 text-coffee-800 hover:bg-coffee-100 rounded-xl transition-colors duration-200 font-medium"
                      data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
                <li className="pt-2">
                  <button
                    onClick={() => scrollToSection('#kapcsolat')}
                    className="w-full bg-coffee-900 text-cream py-3 px-4 rounded-xl font-medium hover:bg-coffee-800 transition-colors duration-200"
                    data-testid="mobile-cta-button"
                  >
                    Kérj ajánlatot
                  </button>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  )
}

export default Navbar
